import React, { useState } from 'react';

import { NonLinearStep } from './non-linear-step/NonLinearStep';
import styles from './NonLinearStepper.module.scss';
import { truncateText } from './utils';

export const isHighlighted = ({
  isSimpleVersion,
  activeIndex,
  index,
  stepsLength,
}: {
  isSimpleVersion: boolean;
  activeIndex?: number;
  index: number;
  stepsLength: number;
}) => {
  if (activeIndex === undefined) {
    return {
      shouldHighlightConnector: false,
      shouldHighlightStep: false,
    };
  }

  if (isSimpleVersion) {
    return {
      shouldHighlightConnector: true,
      shouldHighlightStep: true,
    };
  }

  const isActiveStep = index === activeIndex;
  const isIntermediateStep = index !== 0 && index !== stepsLength - 1;

  const isAdjacentStepActive =
    activeIndex === index - 1 || activeIndex === index + 1;
  const isNextStepActive = activeIndex === index + 1;

  return {
    shouldHighlightConnector: isIntermediateStep
      ? isNextStepActive
      : isActiveStep,
    shouldHighlightStep:
      isActiveStep || (isIntermediateStep && isAdjacentStepActive),
  };
};

export type NonLinearStepType = {
  logoUrl?: string;
  imgNode?: React.ReactNode;
  hoverLabel: string;
  connectorLabel?: string;
  iconColor?: string;
  title?: string;
  description?: string;
  isSimple?: boolean;
  highlightConnector?: boolean;
};

export interface NonLinearStepperProps {
  steps: NonLinearStepType[];
  activeIndex?: number;
  iconSize?: number;
}

export const NonLinearStepper = ({
  steps,
  activeIndex,
  iconSize = 32,
}: NonLinearStepperProps) => {
  const [indexErrorMap, setIndexErrorMap] = useState<{
    [key: number]: boolean;
  }>({});

  if (!steps.length) {
    return null;
  }

  const hasTitles = steps.some(step => step.title);
  const hasDescriptions = steps.some(step => step.description);

  const isSimpleVersion = steps.length === 2;
  return (
    <div className={styles.container}>
      <div
        className={styles.stepAndConnectorWrapper}
        style={{ height: `${iconSize + 18}px` }}
      >
        {steps.map((step, index) => {
          const { shouldHighlightConnector, shouldHighlightStep } =
            isHighlighted({
              isSimpleVersion: step.isSimple ?? isSimpleVersion,
              activeIndex,
              index,
              stepsLength: steps.length,
            });

          return (
            <NonLinearStep
              key={`step-${index}`}
              index={index}
              iconSize={iconSize}
              step={step}
              activeIndex={activeIndex}
              shouldHighlightConnector={
                step.highlightConnector ?? shouldHighlightConnector
              }
              shouldHighlightStep={shouldHighlightStep}
              indexErrorMap={indexErrorMap}
              setIndexErrorMap={setIndexErrorMap}
            />
          );
        })}
      </div>
      {hasTitles && (
        <div className={styles.flexRow}>
          {steps.map((step, idx) => (
            <div
              key={idx}
              className={styles.labelWrapper}
              style={{ width: `${iconSize + 18}px` }}
            >
              {step.title && (
                <div className={styles.labelText}>
                  <span className={styles.styledTitle}>
                    {truncateText(step.title)}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {hasDescriptions && (
        <div className={styles.flexRow}>
          {steps.map((step, idx) => (
            <div
              key={idx}
              className={styles.labelWrapper}
              style={{ width: `${iconSize + 18}px` }}
            >
              {step.description && (
                <div className={styles.labelText}>
                  <span className={styles.styledDescription}>
                    {step.description}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
